
import React from 'react';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ModalOverlay from '@/components/ModalOverlay/ModalOverlay';
import CloseButton from '@/components/Buttons/CloseButton/CloseButton';

export interface BookChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Opens Nuitee whitelabel (same as legacy header Book). */
  onSelectBookOnline: () => void;
  /** Opens Typeform book-with-advisor flow (same as pancake menu). */
  onSelectBookWithAdvisor: () => void;
}

const ICON_AREA_CLASSES =
  'flex min-h-[140px] w-full items-center justify-center rounded-2xl bg-gradient-to-b from-white/90 to-[#F4F4F6] ring-1 ring-black/[0.06]';

/**
 * Mini confirmation: Book online (whitelabel) vs Book with advisor (Typeform).
 */
const BookChoiceModal: React.FC<BookChoiceModalProps> = ({
  isOpen,
  onClose,
  onSelectBookOnline,
  onSelectBookWithAdvisor,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClose={onClose} animate presentation="dialog" className="modal-overlay--book-choice">
      <div
        className="book-choice-modal relative mx-4 w-[min(100%,640px)] max-h-[90dvh] overflow-y-auto rounded-[20px] border-2 border-[#FF7629] bg-gradient-to-br from-[#FFF5F0] via-white to-[#F3F0FF] p-6 shadow-[0px_0px_20px_rgba(0,0,0,0.15)] md:p-8"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-choice-modal-title"
      >
        <CloseButton
          onClick={onClose}
          ariaLabel="Close booking options"
          size={24}
          className="absolute right-4 top-4 z-10 text-[#252528] md:right-6 md:top-6"
        />

        <div className="flex flex-col items-center gap-2 pb-6 pt-2 text-center md:gap-3 md:pb-8">
          <h2
            id="book-choice-modal-title"
            className="font-lora text-xl font-semibold tracking-wide text-[#252528] md:text-2xl"
          >
            How would you like to book?
          </h2>
          <p className="max-w-md font-manrope text-sm text-[#525257] md:text-base">
            Browse and book hotels online, or connect with a travel advisor for a personalized trip.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          <button
            type="button"
            onClick={onSelectBookOnline}
            className="flex flex-col items-center gap-4 rounded-2xl bg-white/50 p-4 text-left shadow-sm ring-1 ring-black/5 transition hover:bg-white/80 hover:ring-black/10 md:p-6"
          >
            <div className={ICON_AREA_CLASSES} aria-hidden="true">
              <HotelOutlinedIcon sx={{ fontSize: 72, color: '#252528' }} />
            </div>
            <span className="font-manrope text-base font-semibold text-[#252528] md:text-lg">Book Online</span>
          </button>

          <button
            type="button"
            onClick={onSelectBookWithAdvisor}
            className="flex flex-col items-center gap-4 rounded-2xl bg-white/50 p-4 text-left shadow-sm ring-1 ring-black/5 transition hover:bg-white/80 hover:ring-black/10 md:p-6"
          >
            <div className={ICON_AREA_CLASSES} aria-hidden="true">
              <SupportAgentOutlinedIcon sx={{ fontSize: 72, color: '#252528' }} />
            </div>
            <span className="text-center font-manrope text-base font-semibold text-[#252528] md:text-lg">
              Book with Advisor
            </span>
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default BookChoiceModal;
